import { Injectable } from '@angular/core';
import {
  PortableGameConfig,
  PortableGameConfigSchemaVersion,
  PortableGameConfigSchemaVersion as SchemaVersionConstant,
  PortableMediaReference,
  PortableTeamConfig,
  PortableRoundConfig
} from './portable-config.models';
import { ThemeVisualBlockKeys, isAllowedThemeVisualArtifact } from './theme-visuals';
import { validateThemeVariable } from './theme-variables';

export interface PortableConfigValidationResult {
  isValid: boolean;
  errors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PortableConfigValidatorService {
  private readonly displayObjectsByScope: Record<string, Record<string, string[]>> = {
    Team1: {
      PageSurface: ['Background'],
      Team1CurrentPlayersContent: ['CurrentPlayer', 'Label', 'Surface', 'Team1TeamMembers', 'TeamMemberLabel'],
      Team1GameStatusContent: ['ConnectStatus', 'DeathStatus', 'LivesStatus', 'Surface', 'Team1Score'],
      Team1HeroContent: ['CompanyName', 'RoundNumber', 'RoundType', 'Surface', 'TeamName'],
      Team1QuestionContent: ['Label', 'QuestionValue', 'Surface'],
      Team1StatusContent: ['Label', 'Message', 'Surface']
    },
    Team2: {
      PageSurface: ['Background'],
      Team2CurrentPlayersContent: ['CurrentPlayer', 'Label', 'Surface', 'Team2TeamMembers', 'TeamMemberLabel'],
      Team2GameStatusContent: ['ConnectStatus', 'DeathStatus', 'LivesStatus', 'Surface', 'Team2Score'],
      Team2HeroContent: ['CompanyName', 'RoundNumber', 'RoundType', 'Surface', 'TeamName'],
      Team2QuestionContent: ['Label', 'QuestionValue', 'Surface'],
      Team2StatusContent: ['Label', 'Message', 'Surface']
    },
    Projector: {
      AnswersContent: ['AnswerValue', 'Label', 'Surface'],
      HeroContent: ['CompanyName', 'EventName', 'RoundNumber', 'RoundType', 'Surface', 'TeamName'],
      PageSurface: ['Background'],
      QuestionContent: ['Label', 'QuestionValue', 'Surface'],
      Team1CurrentPlayersContent: ['Label', 'Surface', 'Team1Players'],
      Team2CurrentPlayersContent: ['Label', 'Surface', 'Team2Players'],
      TeamPlayersContent: ['Surface'],
      TeamScores: ['ConnectStatus', 'Label', 'Surface', 'Team1Card', 'Team1CardCurrentPlayer', 'Team1CardScore', 'Team1CardTeamName', 'Team1DeathStatus', 'Team1LivesStatus', 'Team2Card', 'Team2CardCurrentPlayer', 'Team2CardScore', 'Team2CardTeamName', 'Team2DeathStatus', 'Team2LivesStatus']
    },
    EORSummary: {
      AnswersContent: ['Answers', 'Surface'],
      PageSurface: ['Background'],
      ProjectorRoundSummary: ['Surface'],
      ProjectSummaryHeader: ['CompanyName', 'RoundNumber', 'RoundSummary'],
      QuestionContent: ['QuestionValue', 'Surface'],
      Team1AnswerSummary: ['Answers', 'Surface'],
      Team1Summary: ['Surface', 'TeamName', 'TeamScore', 'TeamTotals'],
      Team2AnswerSummary: ['Answers', 'Surface'],
      Team2Summary: ['Surface', 'TeamName', 'TeamScore', 'TeamTotals']
    },
    EOGSummary: {
      GameSummary: ['AnswerMetrics', 'Label', 'Surface'],
      PageSurface: ['Background'],
      ProjectorRoundSummary: ['Surface'],
      ProjectSummaryHeader: ['CompanyName', 'EndOfGameSummary', 'TotalRounds'],
      Team1Summary: ['AnswerMetrics', 'Label', 'Surface', 'TeamName', 'TeamTotalScore', 'WinnerGame', 'WinnerPlayer'],
      Team2Summary: ['AnswerMetrics', 'Label', 'Surface', 'TeamName', 'TeamTotalScore', 'WinnerGame', 'WinnerPlayer']
    }
  };

  private readonly statefulDisplayObjects = new Set([
    'Team1|Team1HeroContent|Surface',
    'Team2|Team2HeroContent|Surface',
    'Projector|TeamScores|Team1Card',
    'Projector|TeamScores|Team2Card'
  ]);

  validate(config: PortableGameConfig): PortableConfigValidationResult {
    const errors: string[] = [];

    this.validateSchema(config.schemaVersion, errors);
    this.validateEvent(config, errors);
    this.validateTeams(config.teams, errors);
    this.validateRounds(config.rounds, config.event.numberOfRounds, errors);
    this.validatePreferences(config.gamePreferences, errors);
    this.validateTheme(config.theme, errors);

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private validateSchema(schemaVersion: PortableGameConfigSchemaVersion, errors: string[]): void {
    if (schemaVersion !== SchemaVersionConstant) {
      errors.push(`Unsupported schema version '${schemaVersion}'.`);
    }
  }

  private validateEvent(config: PortableGameConfig, errors: string[]): void {
    if (!config.event.name.trim()) {
      errors.push('Event name is required.');
    }

    if (config.event.numberOfRounds < 1) {
      errors.push('Event numberOfRounds must be at least 1.');
    }
  }

  private validateTeams(teams: [PortableTeamConfig, PortableTeamConfig], errors: string[]): void {
    if (teams.length !== 2) {
      errors.push('Exactly two teams are required.');
      return;
    }

    const teamNumbers = new Set<number>();
    for (const team of teams) {
      if (!team.name.trim()) {
        errors.push(`Team ${team.teamNumber} name is required.`);
      }

      if (teamNumbers.has(team.teamNumber)) {
        errors.push(`Duplicate team number '${team.teamNumber}' found.`);
      }
      teamNumbers.add(team.teamNumber);

      const memberOrderNumbers = new Set<number>();
      for (const member of team.members) {
        if (member.orderNumber < 1) {
          errors.push(`Team ${team.teamNumber} contains member with invalid order '${member.orderNumber}'.`);
        }

        if (memberOrderNumbers.has(member.orderNumber)) {
          errors.push(`Team ${team.teamNumber} has duplicate member order '${member.orderNumber}'.`);
        }
        memberOrderNumbers.add(member.orderNumber);

        if (!member.displayName.trim()) {
          errors.push(`Team ${team.teamNumber} member at order '${member.orderNumber}' requires displayName.`);
        }
      }
    }

    if (!teamNumbers.has(1) || !teamNumbers.has(2)) {
      errors.push('Team numbers must be 1 and 2.');
    }
  }

  private validateTheme(theme: PortableGameConfig['theme'], errors: string[]): void {
    const allowedThemeFields = new Set([
      'name', 'key', 'media', 'visuals', 'theme-variables',
      'Team1', 'Team2', 'Projector', 'EORSummary', 'EOGSummary'
    ]);
    for (const field of Object.keys(theme)) {
      if (!allowedThemeFields.has(field)) {
        errors.push(`theme.${field} is not supported.`);
      }
    }

    for (const [name, value] of Object.entries(theme['theme-variables'] ?? {})) {
      const message = validateThemeVariable(name, value);
      if (message) {
        errors.push(`theme.theme-variables.${name}: ${message}`);
      }
    }

    this.validateDisplayScopes(theme, errors);

    const media = theme.media as PortableGameConfig['theme']['media'] & { backgrounds?: unknown };
    if (media.backgrounds !== undefined) {
      errors.push('theme.media.backgrounds is not supported; use theme.visuals.backgrounds.');
    }

    const allowedBackgroundRoles = new Set(['team', 'team1', 'team2', 'projector', 'roundSummary', 'endOfGameSummary']);
    for (const [key, reference] of Object.entries(theme.visuals.backgrounds ?? {})) {
      if (!allowedBackgroundRoles.has(key)) {
        errors.push(`theme.visuals.backgrounds.${key} is not a supported role.`);
        continue;
      }
      this.validateVisualReference(reference, `theme.visuals.backgrounds.${key}`, 'image', errors);
    }

    for (const key of Object.keys(theme.visuals.blocks)) {
      if (!(ThemeVisualBlockKeys as readonly string[]).includes(key)) {
        errors.push(`theme.visuals.blocks.${key} is not a supported visual block.`);
      }
    }

    for (const key of ThemeVisualBlockKeys) {
      const block = theme.visuals.blocks[key];
      if (!block) {
        errors.push(`theme.visuals.blocks.${key} is required.`);
        continue;
      }

      this.validateVisualReference(block.image, `theme.visuals.blocks.${key}.image`, 'image', errors);
      this.validateVisualReference(block.svg, `theme.visuals.blocks.${key}.svg`, 'svg', errors);
    }
  }

  private validateDisplayScopes(theme: PortableGameConfig['theme'], errors: string[]): void {
    for (const [scopeName, scope] of Object.entries(this.displayObjectsByScope)) {
      const value = (theme as unknown as Record<string, unknown>)[scopeName];
      if (value === undefined) {
        continue;
      }

      if (!this.isRecord(value)) {
        errors.push(`theme.${scopeName} must be an object.`);
        continue;
      }

      if (Object.keys(value).length === 0) {
        continue;
      }

      if (!this.isRecord(value['sections'])) {
        errors.push(`theme.${scopeName} must contain a sections object.`);
        continue;
      }

      for (const field of Object.keys(value)) {
        if (field !== 'sections') {
          errors.push(`theme.${scopeName}.${field} is not supported.`);
        }
      }

      const sections = value['sections'];
      for (const [sectionName, sectionValue] of Object.entries(sections)) {
        const allowedSections = scope;
        const allowedObjects = allowedSections[sectionName];
        if (!allowedObjects || !this.isRecord(sectionValue) || !this.isRecord(sectionValue['objects'])) {
          errors.push(`theme.${scopeName}.sections.${sectionName} is not a supported section.`);
          continue;
        }

        for (const field of Object.keys(sectionValue)) {
          if (field !== 'objects') {
            errors.push(`theme.${scopeName}.sections.${sectionName}.${field} is not supported.`);
          }
        }

        const objects = sectionValue['objects'];
        for (const [objectName, objectValue] of Object.entries(objects)) {
          const path = `theme.${scopeName}.sections.${sectionName}.objects.${objectName}`;
          if (!allowedObjects.includes(objectName) || !this.isRecord(objectValue) || !this.isRecord(objectValue['values'])) {
            errors.push(`${path} is not a supported display object with values.`);
            continue;
          }

          for (const field of Object.keys(objectValue)) {
            if (!['values', 'selected', 'unselected'].includes(field)) {
              errors.push(`${path}.${field} is not supported.`);
            }
          }

          const objectKey = `${scopeName}|${sectionName}|${objectName}`;
          if (!this.statefulDisplayObjects.has(objectKey)
            && (objectValue['selected'] != null || objectValue['unselected'] != null)) {
            errors.push(`${path} does not support selected/unselected values.`);
          }

          this.validateDisplayValues(objectValue['values'], this.allowedFields(scopeName, sectionName, objectName, false), `${path}.values`, errors);
          for (const state of ['selected', 'unselected'] as const) {
            const stateValue = objectValue[state];
            if (stateValue == null) {
              continue;
            }
            if (!this.isRecord(stateValue)) {
              errors.push(`${path}.${state} must be an object or null.`);
              continue;
            }
            this.validateDisplayValues(stateValue, this.allowedFields(scopeName, sectionName, objectName, true), `${path}.${state}`, errors);
          }
        }
      }
    }
  }

  private allowedFields(scope: string, section: string, object: string, conditional: boolean): Set<string> {
    if (conditional) {
      return new Set(['backgroundColor', 'border.color']);
    }

    const objectKey = `${section}|${object}`;
    const specificFields: Record<string, string[]> = {
      'PageSurface|Background': ['imageOpacityPercent'],
      'Team1GameStatusContent|DeathStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'Team1GameStatusContent|LivesStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'Team2GameStatusContent|DeathStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'Team2GameStatusContent|LivesStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'TeamScores|Team1DeathStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'TeamScores|Team1LivesStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'TeamScores|Team2DeathStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'TeamScores|Team2LivesStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'Team1HeroContent|Surface': ['backgroundColor', 'border.color', 'border.cornerStyle', 'border.thicknessPx'],
      'Team2HeroContent|Surface': ['backgroundColor', 'border.color', 'border.cornerStyle', 'border.thicknessPx'],
      'TeamScores|Team1Card': ['border.color', 'border.cornerStyle', 'border.thicknessPx'],
      'TeamScores|Team2Card': ['border.color', 'border.cornerStyle', 'border.thicknessPx'],
      'ProjectorRoundSummary|Surface': ['backgroundColor', 'border.color', 'border.cornerStyle', 'border.thicknessPx'],
      'Team1Summary|WinnerGame': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'Team2Summary|WinnerGame': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'Team1Summary|WinnerPlayer': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
      'Team2Summary|WinnerPlayer': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source']
    };
    const fields = new Set(specificFields[objectKey] ?? ['backgroundColor', 'border.color', 'border.cornerStyle', 'border.thicknessPx', 'font.color', 'font.sizeRem']);
    if (object === 'Surface') {
      fields.delete('font.color');
      fields.delete('font.sizeRem');
    }
    return fields;
  }

  private validateDisplayValues(value: Record<string, unknown>, allowedFields: Set<string>, path: string, errors: string[]): void {
    const topLevelFields: Record<string, string[]> = {
      backgroundColor: ['backgroundColor'],
      imageOpacityPercent: ['imageOpacityPercent'],
      border: ['border.color', 'border.cornerStyle', 'border.thicknessPx'],
      font: ['font.color', 'font.sizeRem'],
      svg: ['svg.source', 'svg.sizePx', 'svg.direction', 'svg.color', 'svg.rotationDegrees']
    };

    for (const field of Object.keys(value)) {
      if (!topLevelFields[field]) {
        errors.push(`${path}.${field} is not a supported display property.`);
      }
    }

    const checkAllowed = (field: string, propertyValue: unknown): void => {
      if (propertyValue != null && !allowedFields.has(field)) {
        errors.push(`${path}.${field} is not permitted for this display object.`);
      }
    };
    checkAllowed('backgroundColor', value['backgroundColor']);
    checkAllowed('imageOpacityPercent', value['imageOpacityPercent']);

    const border = this.readNestedRecord(value['border'], `${path}.border`, errors);
    if (border) {
      for (const field of Object.keys(border)) {
        if (!['color', 'cornerStyle', 'thicknessPx'].includes(field)) {
          errors.push(`${path}.border.${field} is not supported.`);
        }
      }
      checkAllowed('border.color', border['color']);
      checkAllowed('border.cornerStyle', border['cornerStyle']);
      checkAllowed('border.thicknessPx', border['thicknessPx']);
      this.validateColorValue(border['color'], `${path}.border.color`, errors);
      if (border['cornerStyle'] != null && !['Square', 'Rounded', 'Pill'].includes(String(border['cornerStyle']))) {
        errors.push(`${path}.border.cornerStyle must be Square, Rounded, or Pill.`);
      }
      if (border['thicknessPx'] != null && (typeof border['thicknessPx'] !== 'number' || border['thicknessPx'] < 0)) {
        errors.push(`${path}.border.thicknessPx must be non-negative.`);
      }
    }

    const font = this.readNestedRecord(value['font'], `${path}.font`, errors);
    if (font) {
      for (const field of Object.keys(font)) {
        if (!['color', 'sizeRem'].includes(field)) {
          errors.push(`${path}.font.${field} is not supported.`);
        }
      }
      checkAllowed('font.color', font['color']);
      checkAllowed('font.sizeRem', font['sizeRem']);
      this.validateColorValue(font['color'], `${path}.font.color`, errors);
      if (font['sizeRem'] != null && (typeof font['sizeRem'] !== 'number' || font['sizeRem'] <= 0)) {
        errors.push(`${path}.font.sizeRem must be greater than zero.`);
      }
    }

    const svg = this.readNestedRecord(value['svg'], `${path}.svg`, errors);
    if (svg) {
      for (const field of Object.keys(svg)) {
        if (!['source', 'sizePx', 'direction', 'color', 'rotationDegrees'].includes(field)) {
          errors.push(`${path}.svg.${field} is not supported.`);
        }
      }
      for (const field of ['source', 'sizePx', 'direction', 'color', 'rotationDegrees']) {
        checkAllowed(`svg.${field}`, svg[field]);
      }
      if (svg['rotationDegrees'] != null) {
        errors.push(`${path}.svg.rotationDegrees is not defined by the workbook contract.`);
      }
      if (svg['source'] != null && (typeof svg['source'] !== 'string'
        || (!/^https?:\/\/\S+$/i.test(svg['source'])
          && !/^data:image\/svg\+xml;base64,[A-Za-z0-9+/]+={0,2}$/i.test(svg['source'])))) {
        errors.push(`${path}.svg.source must be an HTTP(S) URL or base64 SVG data URI.`);
      }
      if (svg['sizePx'] != null && (typeof svg['sizePx'] !== 'number' || svg['sizePx'] <= 0)) {
        errors.push(`${path}.svg.sizePx must be greater than zero.`);
      }
      if (svg['direction'] != null && !['Horizontal', 'Vertical'].includes(String(svg['direction']))) {
        errors.push(`${path}.svg.direction must be Horizontal or Vertical.`);
      }
      this.validateColorValue(svg['color'], `${path}.svg.color`, errors);
    }

    this.validateColorValue(value['backgroundColor'], `${path}.backgroundColor`, errors);
    if (value['imageOpacityPercent'] != null
      && (typeof value['imageOpacityPercent'] !== 'number' || value['imageOpacityPercent'] < 0 || value['imageOpacityPercent'] > 100)) {
      errors.push(`${path}.imageOpacityPercent must be between 0 and 100.`);
    }
  }

  private readNestedRecord(value: unknown, path: string, errors: string[]): Record<string, unknown> | null {
    if (value == null) {
      return null;
    }
    if (!this.isRecord(value)) {
      errors.push(`${path} must be an object or null.`);
      return null;
    }
    return value;
  }

  private validateColorValue(value: unknown, path: string, errors: string[]): void {
    if (value == null) {
      return;
    }
    if (typeof value !== 'string' || !/^(#[0-9a-f]{3,8}|rgba?\([0-9.,%\s]+\)|hsla?\([0-9.,%\s]+\))$/i.test(value)) {
      errors.push(`${path} must be a hex, rgb(a), or hsl(a) color.`);
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private validateVisualReference(
    reference: PortableMediaReference | null,
    path: string,
    layer: 'image' | 'svg',
    errors: string[]
  ): void {
    if (!reference) {
      return;
    }

    if (!reference.fileName.trim() || /[\\/]/.test(reference.fileName)) {
      errors.push(`${path}.fileName must be a file name without a local path.`);
      return;
    }

    if (reference.url && /^(?:[a-z][a-z\d+.-]*:|[\\/]{2}|[\\/]|[a-z]:[\\/])/i.test(reference.url)) {
      errors.push(`${path}.url must not contain an absolute or remote URL.`);
    }

    if (!isAllowedThemeVisualArtifact({
      fileName: reference.fileName,
      contentType: reference.contentType ?? ''
    }, layer)) {
      errors.push(`${path} is not a supported ${layer} reference.`);
    }
  }

  private validateRounds(rounds: PortableRoundConfig[], expectedRoundCount: number, errors: string[]): void {
    if (rounds.length === 0) {
      errors.push('At least one round is required.');
      return;
    }

    const roundNumbers = new Set<number>();

    for (const round of rounds) {
      if (round.roundNumber < 1) {
        errors.push(`Round '${round.roundNumber}' is invalid.`);
      }

      if (roundNumbers.has(round.roundNumber)) {
        errors.push(`Duplicate round number '${round.roundNumber}' found.`);
      }
      roundNumbers.add(round.roundNumber);

      if (!round.question.text.trim()) {
        errors.push(`Round '${round.roundNumber}' question text is required.`);
      }

      const populatedAnswers = round.question.answers.filter(answer => !!answer.text.trim());
      if (populatedAnswers.length === 0) {
        errors.push(`Round '${round.roundNumber}' must include at least one answer with text.`);
      }

      const answerOrderNumbers = new Set<number>();
      for (const answer of populatedAnswers) {
        if (answer.orderNumber < 1) {
          errors.push(`Round '${round.roundNumber}' has answer with invalid order '${answer.orderNumber}'.`);
        }

        if (answerOrderNumbers.has(answer.orderNumber)) {
          errors.push(`Round '${round.roundNumber}' has duplicate answer order '${answer.orderNumber}'.`);
        }
        answerOrderNumbers.add(answer.orderNumber);

        if (answer.points < 0) {
          errors.push(`Round '${round.roundNumber}' answer '${answer.orderNumber}' points cannot be negative.`);
        }
      }
    }

    if (expectedRoundCount > 0 && rounds.length > expectedRoundCount) {
      errors.push(`Rounds count (${rounds.length}) exceeds event numberOfRounds (${expectedRoundCount}).`);
    }
  }

  private validatePreferences(config: PortableGameConfig['gamePreferences'], errors: string[]): void {
    if (config.gamePlayRulePreference !== 1 && config.gamePlayRulePreference !== 2) {
      errors.push('gamePlayRulePreference must be 1 or 2.');
    }

    if (config.maxWrongAttempts < 1) {
      errors.push('maxWrongAttempts must be at least 1.');
    }

    if (config.WinnerTimerValue !== undefined
      && (!Number.isInteger(config.WinnerTimerValue) || config.WinnerTimerValue < 2 || config.WinnerTimerValue > 10)) {
      errors.push('WinnerTimerValue must be an integer between 2 and 10.');
    }

    if (config.timers) {
      if (config.timers.podium.seconds < 1) {
        errors.push('timers.podium.seconds must be at least 1.');
      }

      if (config.timers.answer.seconds < 1) {
        errors.push('timers.answer.seconds must be at least 1.');
      }
    }
  }
}
